// React hooks
import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Helpers i API servisi
import { useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";
import {
  useHandleDateChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import {
  createFleetVehicleExpenseAPI,
  getFleetVehicleExpenseAPI,
  updateFleetVehicleExpenseAPI,
} from "services/api/fleet/vehicles/expenses/api";
import { transformFleetVehicleExpenseEditFormValues } from "services/api/fleet/vehicles/expenses/transformations";

// Tipovi i konstante
import { Errors } from "services/forms/validation/types";
import { FleetVehicleExpenseEditFormValues } from "services/api/fleet/vehicles/expenses/types";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import { initialFleetVehicleExpenseEditForm } from "services/api/fleet/vehicles/expenses/consts";
import { VehicleExpenseEditProps } from "./types";
import {
  FLEET_VEHICLE_EXPENSE_DETAILS_STEP_SCHEMA,
  FLEET_VEHICLE_EXPENSE_EDIT_FORM_SCHEMA,
  FLEET_VEHICLE_EXPENSE_TYPE_STEP_SCHEMA,
} from "./validation";

// UI komponente
import { DatePicker } from "ui/components/DatePicker";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";
import {
  FLEET_VEHICLE_EXPENSE_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_EXPENSE_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import ExpenseIntro from "./components/ExpenseIntro/ExpenseIntro";
import ExpenseTypeStep from "./components/ExpenseTypeStep/ExpenseTypeStep";

const EXPENSE_TYPE_STEP = 0;
const EXPENSE_DETAILS_STEP = 1;

const PDV = 0.25;
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Unos i uređivanje troška vozila u tri koraka: vrsta troška (kartice iz zajedničkog
 * šifrarnika), podaci o trošku i dokumenti.
 */
const VehicleExpenseEditCard: React.FC<VehicleExpenseEditProps> = (props) => {
  const {
    show,
    vehicleId,
    expenseId,
    onSuccess,
    onClose,
    defaultExpenseTypeId,
    isFullScreen = true,
  } = props;

  const { t } = useTranslate();

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);

  const [formValues, setFormValues] = useState<FleetVehicleExpenseEditFormValues>(
    initialFleetVehicleExpenseEditForm
  );

  const [uploads, setUploads] = useState<FleetDocumentUpload[]>([]);

  useEffect(() => {
    if (!show) return;

    // Novi trošak može doći s predodabranom vrstom (npr. iz premještaja vozila).
    setFormValues({
      ...initialFleetVehicleExpenseEditForm,
      expenseTypeId: expenseId ? "" : defaultExpenseTypeId ?? "",
    });
    setUploads([]);
    setErrors({});
    setStepCurrent(0);

    if (expenseId) {
      fetchExpense(expenseId);
    }
  }, [show, expenseId, defaultExpenseTypeId]);

  const fetchExpense = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleExpenseEditForm);
      setUploads([]);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleExpenseAPI(id);
      if (result) {
        setFormValues(transformFleetVehicleExpenseEditFormValues(result));
        if (result.uploads && Array.isArray(result.uploads)) {
          setUploads(result.uploads);
        }
      }
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);

    setErrors({});
  };

  const handleAmountOrTaxChange = useCallback(
    (name: "amount" | "taxAmount", value: number | null) => {
      setFormValues((prev) => {
        if (value === null || isNaN(value)) {
          return {
            ...prev,
            [name]: null,
          };
        }

        if (name === "amount") {
          const taxAmount = round2(value * PDV);
          return {
            ...prev,
            amount: value,
            taxAmount,
          };
        }

        if (name === "taxAmount") {
          const amount = round2(value / PDV);
          return {
            ...prev,
            taxAmount: value,
            amount,
          };
        }

        return prev;
      });
    },
    []
  );

  const handleAmountChange = useCallback(
    (value: number | null) => {
      handleAmountOrTaxChange("amount", value);
    },
    [handleAmountOrTaxChange]
  );

  const handleTaxAmountChange = useCallback(
    (value: number | null) => {
      handleAmountOrTaxChange("taxAmount", value);
    },
    [handleAmountOrTaxChange]
  );

  const handleFormSubmit = useCallback(async () => {
    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_EXPENSE_EDIT_FORM_SCHEMA,
      setErrors
    );

    // Neispravan podatak je na ranijem koraku, pa se vraća na njega da se greška vidi.
    if (!isValid) {
      setStepCurrent(formValues.expenseTypeId ? EXPENSE_DETAILS_STEP : EXPENSE_TYPE_STEP);
      return;
    }

    setIsLoading(true);

    try {
      let result;

      if (expenseId) {
        result = await updateFleetVehicleExpenseAPI(
          vehicleId,
          expenseId,
          formValues,
          uploads,
          t
        );
      } else {
        result = await createFleetVehicleExpenseAPI(
          vehicleId,
          formValues,
          uploads,
          t
        );
      }

      if (result && onSuccess) onSuccess(result);
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }, [formValues, expenseId, vehicleId, uploads, t, onSuccess]);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  const handleUploadSave = useCallback((newUploads: FleetDocumentUpload[]) => {
    setUploads((prev) => [...prev, ...newUploads]);
  }, []);

  const handleUploadDelete = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const handleExpenseTypeChange = useCallback((expenseTypeId: string) => {
    setFormValues((prev) => ({ ...prev, expenseTypeId }));
    setErrors((prev) => ({ ...prev, expenseTypeId: "" }));
  }, []);

  const handleNextStep = useCallback(() => {
    if (stepCurrent === EXPENSE_TYPE_STEP) {
      const isValid = validateYup(formValues, FLEET_VEHICLE_EXPENSE_TYPE_STEP_SCHEMA, setErrors);
      if (!isValid) return;
    }

    if (stepCurrent === EXPENSE_DETAILS_STEP) {
      const isValid = validateYup(formValues, FLEET_VEHICLE_EXPENSE_DETAILS_STEP_SCHEMA, setErrors);
      if (!isValid) return;
    }

    setStepCurrent((step) => step + 1);
  }, [stepCurrent, formValues]);

  const handleBackStep = useCallback(() => {
    setStepCurrent((step) => step - 1);
  }, []);

  const steps = useMemo(
    () => [
      <WizardStep key="type" stepNumber={EXPENSE_TYPE_STEP} stepCurrent={stepCurrent}>
        <ExpenseTypeStep
          value={formValues.expenseTypeId}
          onChange={handleExpenseTypeChange}
          error={errors.expenseTypeId}
        />
      </WizardStep>,
      <WizardStep key="details" stepNumber={EXPENSE_DETAILS_STEP} stepCurrent={stepCurrent}>
        <div className="flex justify-center px-4 pt-8 pb-6">
          <div className="w-full max-w-2xl">
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              Podaci o trošku
            </h1>
            <p className="text-center text-md text-slate-500 mb-6">
              Naziv, iznos i datumi troška.
            </p>
            <form noValidate className="text-left">
              <div className="flex flex-col gap-3 mb-3">
                <div className="flex-1">
                  <TextField
                    type="text"
                    name="name"
                    label={t("admin.fleet.expense.add.name")}
                    placeholder={t("admin.fleet.expense.add.name.placeholder")}
                    onChange={handleTextFieldChange}
                    error={errors.name}
                    value={formValues.name}
                    required
                    endIcon=""
                  />
                </div>
                <div className="flex-1">
                  <TextArea
                    name="description"
                    label={t("admin.fleet.expense.add.description")}
                    placeholder={t("admin.fleet.expense.add.description.placeholder")}
                    onChange={handleTextFieldChange}
                    error={errors.description}
                    value={formValues.description}
                    required
                    endIcon=""
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <DatePicker
                      name="expenseDate"
                      label={t("admin.fleet.expense.add.expensedate")}
                      placeholder={t("admin.fleet.expense.add.expensedate.placeholder")}
                      onChange={handleDateChange}
                      error={errors.expenseDate}
                      value={formValues.expenseDate}
                      width="full"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      name="paymentDate"
                      label={t("admin.fleet.expense.add.paymentdate")}
                      placeholder={t("admin.fleet.expense.add.paymentdate.placeholder")}
                      onChange={handleDateChange}
                      error={errors.paymentDate}
                      value={formValues.paymentDate}
                      width="full"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.expense.add.amount")}
                      name="amount"
                      onChange={handleAmountChange}
                      value={formValues.amount}
                      error={errors.amount}
                      decimals={2}
                      groupAppend="€"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.expense.add.taxamount")}
                      name="taxAmount"
                      onChange={handleTaxAmountChange}
                      value={formValues.taxAmount}
                      error={errors.taxAmount}
                      decimals={2}
                      groupAppend="€"
                      required
                    />
                  </div>
                </div>

              </div>
            </form>
          </div>
        </div>
      </WizardStep>,
      <WizardStep key="documents" stepNumber={2} stepCurrent={stepCurrent}>
        <div className="flex justify-center px-4 pt-8 pb-6">
          <div className="w-full max-w-2xl">
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              Dokumenti
            </h1>
            <p className="text-center text-md text-slate-500 mb-6">
              Priložite račun ili drugi dokument troška. Korak nije obavezan.
            </p>
            <FileUploadControl
              uploads={uploads}
              onUploadSave={handleUploadSave}
              onUploadDelete={handleUploadDelete}
              buttonText={t("admin.fleet.expense.add.upload")}
              modalTitle="Upload dokumenta"
              emptyText="Dodajte datoteke"
            />
          </div>
        </div>
      </WizardStep>,
    ],
    [
      stepCurrent,
      t,
      isFullScreen,
      formValues,
      errors,
      uploads,
      handleExpenseTypeChange,
      handleTextFieldChange,
      handleDateChange,
      handleAmountChange,
      handleTaxAmountChange,
      handleUploadSave,
      handleUploadDelete,
    ]
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      position={isFullScreen ? "side" : "centered"}
      size={isFullScreen ? "full" : "medium"}
      intro={
        isFullScreen
          ? {
              content: (
                <ExpenseIntro
                  formValues={formValues}
                  uploads={uploads}
                  isEdit={!!expenseId}
                  stepCurrent={stepCurrent}
                  stepCount={steps.length}
                />
              ),
            }
          : undefined
      }
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={
          expenseId
            ? FLEET_VEHICLE_EXPENSE_EDIT_PAGE_TITLE.text
            : FLEET_VEHICLE_EXPENSE_CREATE_PAGE_TITLE.text
        }
        onHide={onClose}
      />
      <ModalBody>
        {/* U manjem prozoru sadržaj inače dodiruje rubove, a koraci su različite visine pa
            prozor skače — dodatni razmak i najmanja visina drže ga mirnim. */}
        <div className={isFullScreen ? undefined : "px-6 pb-2 min-h-[460px]"}>
          <Wizard steps={steps} />
        </div>
        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFormSubmit}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(VehicleExpenseEditCard);
